/**
 * OrderLineItem — Renders a single cart item in the checkout order summary.
 * Shows product/plan name, action badge, price or proration breakdown,
 * tax, and policy-aware descriptions (timing, credit info, renewal dates).
 */

import React from 'react';
import { Box, Typography, Chip, Divider, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BoltIcon from '@mui/icons-material/Bolt';

const ACTION_LABELS = {
  subscribe: 'New',
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  lateral: 'Switch',
  activate_free: 'Free',
};

const ACTION_COLORS = {
  subscribe: 'primary',
  upgrade: 'success',
  downgrade: 'warning',
  lateral: 'info',
  activate_free: 'default',
};

function formatCents(cents) {
  if (cents == null) return '—';
  const negative = cents < 0;
  return `${negative ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export default function OrderLineItem({ item, previewItem, onRemove }) {
  const pi = previewItem || {};
  const isDeferred = pi.timing === 'end_of_period';
  const hasProration = pi.proration && pi.proration.prorated_amount_cents !== 0;
  const hasCredit = pi.credit_cents > 0;

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" fontWeight={600}>{pi.planName || item.planName}</Typography>
            <Chip
              label={ACTION_LABELS[item.actionType] || item.actionType}
              color={ACTION_COLORS[item.actionType] || 'default'}
              size="small" variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {pi.productName || item.productName}
            {pi.currentPlanName && ` — from ${pi.currentPlanName}`}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
          <Typography variant="body1" fontWeight={600}>
            {isDeferred ? '$0.00' : formatCents(pi.line_total_cents ?? item.priceCents)}
          </Typography>
          {onRemove && (
            <IconButton size="small" onClick={onRemove} sx={{ mt: -0.5 }} aria-label="Remove item">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Proration breakdown for immediate upgrades with credit */}
      {hasProration && !isDeferred && (
        <Box sx={{ pl: 2, mt: 0.5, bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              Unused time on {pi.currentPlanName} ({pi.proration.days_remaining} days)
            </Typography>
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {formatCents(-pi.proration.unused_credit_cents)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {pi.planName} ({pi.proration.days_remaining} days remaining)
            </Typography>
            <Typography variant="caption">
              +{formatCents(pi.proration.new_charge_cents)}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={600}>
              {pi.proration.prorated_amount_cents > 0 ? 'Due today' : 'Account credit'}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {formatCents(pi.proration.prorated_amount_cents)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Tax */}
      {pi.tax && pi.tax.tax_amount_cents > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 2, display: 'block', mt: 0.5 }}>
          Tax ({pi.tax.state_code} @ {pi.tax.rate_percent}%): +{formatCents(pi.tax.tax_amount_cents)}
        </Typography>
      )}

      {/* Policy description — the key information for the customer */}
      {pi.first_charge_description && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, pl: 2, mt: 0.75 }}>
          {isDeferred
            ? <ScheduleIcon sx={{ fontSize: 14, color: 'warning.main', mt: 0.2 }} />
            : <BoltIcon sx={{ fontSize: 14, color: 'success.main', mt: 0.2 }} />
          }
          <Typography variant="caption" sx={{
            color: isDeferred ? 'warning.dark' : 'text.secondary',
            fontWeight: isDeferred ? 500 : 400,
          }}>
            {pi.first_charge_description}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}
